// frontend/src/pages/login.jsx
import { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { FaGoogle } from 'react-icons/fa'; // Example using react-icons
import Toast from '../components/Toast';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, authLoading, user } = useContext(AuthContext);

  // Get the page the user was trying to access, or default to profile
  const from = location.state?.from?.pathname || '/profile';

  // Redirect after successful login and user data is loaded
  useEffect(() => {
    if (isLoggingIn && !authLoading && isAuthenticated && user) {
      // User is now logged in and data is loaded, navigate to original page
      navigate(from, { replace: true });
      setIsLoggingIn(false);
    }
  }, [isLoggingIn, authLoading, isAuthenticated, user, navigate, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setMessage('');
    setToast(null);
    
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      if (res.ok) {
        const data = await res.json();
        // Wait for login to complete (including user profile fetch)
        await login(data.token);
        // Navigation will happen in useEffect when user data is loaded
      } else {
        // Try to parse error response
        let errorMessage = 'Login failed. Please try again.';
        try {
          const data = await res.json();
          errorMessage = data.error || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = res.statusText || errorMessage;
        }
        setMessage(errorMessage);
        setToast({
          message: errorMessage,
          type: 'error'
        });
        setIsLoggingIn(false);
      }
    } catch (error) {
      // Handle network errors, CORS errors, etc.
      let errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
      
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') ||
          error.message.includes('CORS')) {
        errorMessage = 'Cannot connect to the server. The backend may be down or there is a network issue. Please try again later.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      setMessage(errorMessage);
      setToast({
        message: errorMessage,
        type: 'error'
      });
      setIsLoggingIn(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect the user to the backend endpoint that starts the Google OAuth flow
    window.location.href = `${import.meta.env.VITE_API_URL}/api/auth/google`;
  };

  return (
    <div className="flex items-center justify-center">
      <div className="my-40 bg-white p-8 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-900 max-w-md w-full">
        <h1 className="text-3xl font-heading mb-6 text-center dark:text-white">Welcome Back</h1>
        <form onSubmit={handleLogin} className="space-y-4 font-body">
          <input
            type="email"
            placeholder="Email"
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="submit"
            className="bg-customGreen dark:bg-customGreen dark:text-black w-full py-3 bg-black text-white rounded-md hover:bg-customGreen/90 dark:hover:bg-customGreen/90 transition"
          >
            Login
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center justify-center">
          <span className="border-t border-gray-300 flex-grow"></span>
          <span className="mx-4 text-gray-500 text-sm">OR</span>
          <span className="border-t border-gray-300 flex-grow"></span>
        </div>

        {/* Google Login Button */}
        <button
          onClick={handleGoogleLogin}
          className="w-full py-3 bg-white border border-gray-300 text-gray-700 rounded-md transition flex items-center justify-center hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <FaGoogle className="mr-2" /> {/* Optional icon */}
          Continue with Google
        </button>

        {message && <p className="mt-4 text-center text-red-500 dark:text-red-400">{message}</p>}
        <p className="mt-6 text-center font-body text-black dark:text-white">
          Don't have an account?{' '}
          <Link to="/register" className="font-semibold hover:underline dark:text-white">
            Register here
          </Link>
        </p>
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={7000}
        />
      )}
    </div>
  );
}

export default Login;